import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
  useEffect,
} from "react";
import fetchWithCredentials from "apiClient";
import { Condo } from "types/condo";

interface CondosContextType {
  condos: Condo[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<Condo[]>;
  condosCount: number;
  selectedCondoId: number | null;
  setSelectedCondoId: (condoId: number | null) => void;
  selectedCondoMode: "simulated" | "live" | undefined;
}

const CondosContext = createContext<CondosContextType | undefined>(undefined);

export function CondosProvider({ children }: { children: ReactNode }) {
  const [condos, setCondos] = useState<Condo[]>([]);
  // Start true so consumers do not treat the empty initial portfolio as final
  // (e.g. Home URL hydration rejecting a valid ?condo= before the first fetch).
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCondoId, setSelectedCondoId] = useState<number | null>(null);

  const fetchCondos = useCallback(async (): Promise<Condo[]> => {
    setLoading(true);
    setError(null);
    try {
      const condos = await fetchWithCredentials(`/manager-api/condos/`);
      setCondos(condos);
      return condos as Condo[];
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Error fetching condos");
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCondos();
  }, [fetchCondos]);

  const refresh = useCallback(async () => {
    return await fetchCondos();
  }, [fetchCondos]);

  const condosCount = useMemo(() => condos.length, [condos]);

  const selectedCondoMode = useMemo(() => {
    const selectedCondo = condos.find((c) => c.id === selectedCondoId);
    return selectedCondo?.mode;
  }, [condos, selectedCondoId]);

  return (
    <CondosContext.Provider
      value={{
        condos,
        loading,
        error,
        refresh,
        condosCount,
        selectedCondoId,
        setSelectedCondoId,
        selectedCondoMode,
      }}
    >
      {children}
    </CondosContext.Provider>
  );
}

export function useCondos() {
  const context = useContext(CondosContext);
  if (!context) {
    throw new Error("useCondos must be used within a CondosProvider");
  }
  return context;
}
